import { createStore, select, setProp, setProps, withProps } from '@ngneat/elf';
import { Injectable } from '@angular/core';
import { Observable, shareReplay } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';

import { IMolecule } from './molecule.model';

export interface MoleculesProps {
  searchTerm: string | null;
  letter: string | null;
  molecules: IMolecule[];
  selectedMolecules: IMolecule[];
  selectedMoleculesIDs: number[];
  loading: boolean;
  error: HttpErrorResponse | null;
}

const initialProps: MoleculesProps = {
  searchTerm: null,
  letter: null,
  molecules: [],
  selectedMolecules: [],
  selectedMoleculesIDs: [],
  loading: false,
  error: null,
};

const STORE_NAME = 'sandoz-v2-molecules';

@Injectable({ providedIn: 'root' })
export class MoleculesRepository {
  private readonly store;

  public readonly searchTerm$: Observable<string | null>;
  public readonly letter$: Observable<string | null>;
  public readonly molecules$: Observable<IMolecule[]>;
  public readonly selectedMolecules$: Observable<IMolecule[]>;
  public readonly selectedMoleculesIDs$: Observable<number[]>;
  public readonly loading$: Observable<boolean>;
  public readonly error$: Observable<HttpErrorResponse | null>;

  constructor() {
    this.store = createStore(
      { name: STORE_NAME },
      withProps<MoleculesProps>(initialProps),
    );

    this.searchTerm$ = this.store.pipe(
      select(state => state.searchTerm),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );

    this.letter$ = this.store.pipe(
      select(state => state.letter),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );

    this.molecules$ = this.store.pipe(
      select(state => state.molecules),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );

    this.selectedMolecules$ = this.store.pipe(
      select(state => state.selectedMolecules),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );

    this.selectedMoleculesIDs$ = this.store.pipe(
      select(state => state.selectedMoleculesIDs),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );

    this.loading$ = this.store.pipe(
      select(state => state.loading),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );

    this.error$ = this.store.pipe(
      select(state => state.error),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );
  }

  getStoreProp<K extends keyof MoleculesProps>(key: K): MoleculesProps[K] {
    const value = this.store.getValue()[key];
    if (key === 'selectedMoleculesIDs' && Array.isArray(value)) {
      return value.flat() as MoleculesProps[K];
    }

    return value;
  }

  setStoreProp<K extends keyof MoleculesProps, V extends MoleculesProps[K]>(
    key: K,
    value: V,
  ): void {
    this.store.update(setProp(key, value));
  }

  getSelectedMoleculesIds(): number[] {
    const selectedMolecules = this.store.getValue().selectedMolecules;

    return selectedMolecules.reduce<number[]>((acc, molecule) => {
      if (Array.isArray(molecule.id)) {
        return [...acc, ...molecule.id];
      }

      return [...acc, molecule.id];
    }, []);
  }

  setMolecules(molecules: IMolecule[]): void {
    this.setStoreProp('molecules', molecules);
  }

  setSelectedMolecules(molecules: IMolecule[]): void {
    this.setStoreProp('selectedMolecules', molecules);
    this.setStoreProp('selectedMoleculesIDs', this.getSelectedMoleculesIds());
  }

  setSearchTerm(searchTerm: string): void {
    this.store.update(setProps({ letter: '', searchTerm }));
  }

  setLetter(letter: string): void {
    this.store.update(setProps({ letter, searchTerm: '' }));
  }

  setLoading(loading: boolean): void {
    this.setStoreProp('loading', loading);
  }

  setError(error: HttpErrorResponse | null): void {
    this.setStoreProp('error', error);
  }

  clearSelection(): void {
    this.setStoreProp('selectedMolecules', []);
    this.setStoreProp('selectedMoleculesIDs', []);
  }

  resetStore(): void {
    this.store.reset();
  }
}
